import type { WaitForConfirmationModes } from '@sb/routine-runner';

import { ActionRequiredError } from './errors';

// Local implementations (ported) of Remote Control helpers

export function validateCodeExpression(
  expression:
    | { kind?: string; errorMessage?: string | null }
    | undefined
    | null,
  fieldId?: string,
  name: string | undefined = fieldId,
): void {
  if (
    (expression?.kind === 'JavaScript' || expression?.kind === 'Python') &&
    expression.errorMessage
  ) {
    if (fieldId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `Error in ${name}: ${expression.errorMessage}`,
        fieldId,
      });
    } else {
      throw new Error(`Error in expression: ${expression.errorMessage}`);
    }
  }

  if (expression?.kind === 'JavaScript') {
    throw new ActionRequiredError({
      kind: 'deprecatedConfigurationWarning',
      message: `Expression uses deprecated syntax.`,
      fieldId,
      deprecationKind: 'javaScriptExpressions',
    });
  }
}

type Conditional = {
  variable?:
    | {
        kind: 'step';
        stepID: string;
        stepKind: string;
        variableName: string;
        stepPosition: number;
      }
    | { kind: 'expression' }
    | { kind: string };
  value?: unknown;
};

export function validateConditionalWaitForConfirmationStep(
  condition: Conditional,
  step: {
    stepKind: string;
    args?: { mode: WaitForConfirmationModes; choices: string[] };
  },
): void {
  if (!condition.variable || condition.variable.kind !== 'step') return;

  const { variableName, stepPosition } = condition.variable as any;

  if (step.stepKind !== 'WaitForConfirmation' || step.args == null) {
    throw new Error(
      'Runtime Error: Cannot pass a step that is not `WaitForConfirmation` to this validator.',
    );
  }

  const { mode, choices } = step.args;

  const modeToVariableName: Required<{
    [K in WaitForConfirmationModes]: string;
  }> = {
    confirm: 'confirmed',
    choice: 'choice',
    freeform: 'freeformInput',
  };

  if (modeToVariableName[mode] !== variableName) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `Condition must be updated: Variable "${stepPosition}. Prompt User" is invalid. Update to reflect that the prompt is now of type "${mode}."`,
      fieldId: 'condition',
    });
  }

  if (
    mode === 'choice' &&
    !choices.includes((condition.value || '').toString())
  ) {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `Condition must be updated: Value for variable "${stepPosition}. Prompt User" is invalid. Choice must be updated.`,
      fieldId: 'condition',
    });
  }
}

export function validateConditionalSteps(
  conditions: Conditional[][],
  routine: {
    stepConfigurations: Record<string, { stepKind: string; args?: any }>;
  },
): void {
  for (let i = 0; i < conditions.length; i += 1) {
    for (let j = 0; j < conditions[i].length; j += 1) {
      const condition = conditions[i][j];

      if (condition.variable?.kind !== 'step') continue;

      const stepVar = condition.variable as any;
      const step = routine.stepConfigurations[stepVar.stepID];

      if (!step) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Condition must be updated: Makes use of invalid step.',
          fieldId: 'condition',
        });
      }

      if (step.stepKind !== stepVar.stepKind) {
        throw new Error(
          `Conditional step validation failed: step's kind is different from condition's stepKind. [step.stepKind=${step.stepKind} !== condition.variable.stepKind (${stepVar.stepKind})].`,
        );
      }

      switch (stepVar.stepKind) {
        case 'WaitForConfirmation':
          validateConditionalWaitForConfirmationStep(condition, step as any);
          break;
        default:
          break;
      }
    }
  }
}

export function validateConditionalJavascriptExpressions(
  conditions: Conditional[][],
): void {
  for (let i = 0; i < conditions.length; i += 1) {
    for (let j = 0; j < conditions[i].length; j += 1) {
      const condition = conditions[i][j];

      if (condition.variable?.kind !== 'expression') continue;

      const value = condition?.value as any;

      if (value && typeof value === 'object' && value.kind === 'JavaScript') {
        throw new ActionRequiredError({
          kind: 'deprecatedConfigurationWarning',
          message: 'Condition uses deprecated syntax.',
          fieldId: 'condition',
          deprecationKind: 'javaScriptExpressions',
        });
      }
    }
  }
}

export function validateConditional(
  conditions: Conditional[][],
  routine: {
    stepConfigurations: Record<string, { stepKind: string; args?: any }>;
  },
): void {
  validateConditionalSteps(conditions, routine);
  validateConditionalJavascriptExpressions(conditions);
}

export function validateMotionSpeed(
  stepKind: string,
  motionSpeed?: { customLimits?: unknown },
): void {
  const customLimits = motionSpeed?.customLimits as
    | Record<string, unknown>
    | null
    | undefined;

  if (
    customLimits?.jointAccelerationsPercent ||
    customLimits?.jointSpeedsPercent ||
    customLimits?.tooltipSpeedPercent
  ) {
    throw new Error(
      `${stepKind} step is configured with motion speed custom limits with an outdated configuration.
    Please reconfigure ${stepKind} steps with custom limit speeds.`,
    );
  }
}
